import { render, h } from 'preact';
import { useMemo, createContext } from 'preact/compat';
import { useDonationStep, DonationStepEnum, DonationStepProvider } from '../hooks/step';
import { CartProvider } from '../hooks/cart';
import { UserProvider } from '../hooks/user';
import { VGSProvider } from '../hooks/vgs';
import type { WPConfig } from '../types';

// CSS.
import '../scss/main.scss';

/**
 * Static theme provider.
 */
export const Theme = createContext<WPConfig | undefined>(undefined);

/**
 * Global providers.
 * Used to provide global state to components.
 *
 * This is the primary entry point for building each UI.
 * It should be used as the wrapper for each UI in pages.
 */
export const GlobalProvider = ({
	children,
}: {
	children: h.JSX.Element,
}) => {
	/**
	 * Parse local WP data.
	 */
	const config = useMemo(() => window.EM_EA_DATA as WPConfig, []);

	if (!config) {
		console.error('Page config is unavailable.');
	}

	return (
		<Theme.Provider value={config}>
			<DonationStepProvider>
				<CartProvider>
					<UserProvider>
						<VGSProvider>
							<section id="ea-entry" className="ea-integration">
								{children}
							</section>
						</VGSProvider>
					</UserProvider>
				</CartProvider>
			</DonationStepProvider>
		</Theme.Provider>
	);
};

/**
 * Render Step Conditional.
 * Must be called within a <GlobalProvider> context.
 *
 * @param props.step The step to test against.
 * @param props.inclusive Whether to show during the step or to show when not on the step.
 */
export const StepCondition = ({
	children,
	step,
	inclusive = true,
}:{
	children: h.JSX.Element,
	step: DonationStepEnum,
	inclusive?: boolean,
}) => {
	const d = useDonationStep();
	if (inclusive) {
		return d.state === step ? children : null;
	}
	return d.state !== step ? children : null;
};

/**
 * Render app entry.
 */
function renderView(UI: h.JSX.Element) {
	const App = () => (
		<GlobalProvider>
			{UI}
		</GlobalProvider>
	);
	const root = document.getElementById('ea-root');
	render(<App />, root);
}

export default renderView;
